import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import OwnerPageLayout from '../../components/layout/OwnerPageLayout';
import Toggle, { VisibilityOption } from '../../components/profile/Toggle';
import {
    getMySportProfile, updateVisibility, getMarketplaceProducts,
    getMyPurchases, purchasePromotion, submitVerification,
} from '../../services/marketplaceApi';
import '../../styles/marketplace.css';

function getProductIcon(code) {
    if (code === 'search_top') return '⭐';
    if (code === 'featured') return '✨';
    return '🚀';
}

function formatDate(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString();
}

export default function MarketplaceSettingsPage() {
    const { t } = useTranslation(['marketplace', 'common']);
    const [profile, setProfile] = useState(null);
    const [products, setProducts] = useState([]);
    const [purchases, setPurchases] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [p, pr, pu] = await Promise.all([
                getMySportProfile(),
                getMarketplaceProducts('all'),
                getMyPurchases(),
            ]);
            setProfile(p);
            setProducts(pr || []);
            setPurchases((pu || []).filter((x) => x.status === 'active'));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    async function onVisibility(patch) {
        const res = await updateVisibility({
            isPublic: patch.isPublic ?? profile.isPublic,
            isSearchable: patch.isSearchable ?? profile.isSearchable,
            showContacts: patch.showContacts ?? profile.showContacts,
            showInRegionSearch: patch.showInRegionSearch ?? profile.showInRegionSearch,
        });
        setProfile((prev) => ({ ...prev, ...res, ...patch }));
    }

    async function purchase(productId) {
        const res = await purchasePromotion({ productId, targetType: 'profile', profileId: profile.id });
        if (res.paymentUrl) window.location.href = res.paymentUrl;
    }

    async function submitProfileVerification() {
        const fd = new FormData();
        fd.append('entityType', 'sport_profile');
        fd.append('sportProfileId', String(profile.id));
        await submitVerification(fd);
        alert(t('marketplace:verification.pending'));
        load();
    }

    if (loading || !profile) {
        return <OwnerPageLayout title={t('marketplace:visibility.title')}><p>{t('common:loading')}</p></OwnerPageLayout>;
    }

    return (
        <OwnerPageLayout title={t('marketplace:visibility.title')} maxWidth={720} className="mp-settings">
            <Link to="/profile/sport" className="sp-btn sp-btn--outline" style={{ marginBottom: 16 }}>{t('marketplace:profileEdit.title')}</Link>

            <div className="visibility-card">
                <div className="visibility-header">
                    <div>
                        <h3>{t('marketplace:visibility.showInSearch')}</h3>
                        <p>{t('marketplace:visibility.showInSearchDesc')}</p>
                    </div>
                    <Toggle
                        size="large"
                        checked={profile.isPublic}
                        onChange={(v) => onVisibility({ isPublic: v, isSearchable: v })}
                    />
                </div>
                {profile.isPublic && (
                    <div className="visibility-details">
                        <VisibilityOption
                            label={t('marketplace:visibility.showContacts')}
                            desc={t('marketplace:visibility.showContactsDesc')}
                            checked={profile.showContacts}
                            onChange={(v) => onVisibility({ showContacts: v })}
                        />
                        <VisibilityOption
                            label={t('marketplace:visibility.showInRegion')}
                            desc={t('marketplace:visibility.showInRegionDesc')}
                            checked={profile.showInRegionSearch}
                            onChange={(v) => onVisibility({ showInRegionSearch: v })}
                        />
                    </div>
                )}
            </div>

            <div className="completion-card">
                <h3>{t('marketplace:profileEdit.completionLabel', { percent: profile.completionPercent })}</h3>
                <div className="mp-completion__bar"><div className="mp-completion__fill" style={{ width: `${profile.completionPercent}%` }} /></div>
                {profile.completionPercent < 80 && <p>💡 {t('marketplace:profileEdit.completionHint80')}</p>}
            </div>

            {profile.isPublic && (
                <div className="promotion-section">
                    <h3>🚀 {t('marketplace:promotion.title')}</h3>
                    <p>{t('marketplace:promotion.desc')}</p>
                    {products.map((product) => {
                        const active = purchases.find((p) => p.productId === product.id);
                        return (
                            <div key={product.id} className="mp-product-card">
                                <div className="mp-product-header">
                                    <span style={{ fontSize: 24 }}>{getProductIcon(product.code)}</span>
                                    <div className="product-info">
                                        <h4>{product.name}</h4>
                                        <p>{product.description}</p>
                                    </div>
                                    <div className="mp-product-price">
                                        <span>{product.priceRub} ₽</span>
                                        <span className="price-period">/{product.durationDays} {t('marketplace:promotion.days')}</span>
                                    </div>
                                </div>
                                {active ? (
                                    <div className="mp-product-active">✅ {t('marketplace:promotion.active', { date: formatDate(active.expiresAt) })}</div>
                                ) : (
                                    <button type="button" className="sp-btn sp-btn--primary" onClick={() => purchase(product.id)}>{t('marketplace:promotion.buy')}</button>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="verification-card">
                <h3>{profile.verificationStatus === 'verified' ? `✅ ${t('marketplace:verification.approved')}` : `🔍 ${t('marketplace:verification.title')}`}</h3>
                <p>{t('marketplace:verification.desc')}</p>
                {profile.verificationStatus !== 'verified' && (
                    <button type="button" className="sp-btn sp-btn--primary" onClick={submitProfileVerification}>{t('marketplace:verification.submit')}</button>
                )}
            </div>
        </OwnerPageLayout>
    );
}
